import React, { useCallback, useRef, useState } from 'react';
import { ChevronDown, Check, GitBranch } from 'lucide-react';
import { Spinner } from '../../design-system';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints, repoBranchesEndpoint } from '../../utils/api';
import { GitProvider, credentialsInvalidMessage, isFormProvider } from '../../config/providers';
import { ApplicationResponse } from '../../types/application';
import toast from '../../design-system/toast';
import PopoverPortal from '../common/PopoverPortal';

interface BranchSelectorProps {
  organizationId: string;
  applicationId: string;
  repoFullName: string;
  provider: GitProvider;
  currentBranch: string;
  onBranchUpdated: (updatedApplication: ApplicationResponse) => void;
  /** When provider is gitlab and org has multiple instances, pass the repo's instance base URL (e.g. from repo.html_url). */
  gitlabInstanceUrl?: string;
  readOnly?: boolean;
  /** 'badge' (default) renders an inline pill; 'sidebar' renders a full-width row with a "BRANCH" eyebrow. */
  variant?: 'badge' | 'sidebar';
  /** Sidebar variant only: collapse to an icon-only trigger. */
  collapsed?: boolean;
}

interface BranchEntry {
  name: string;
}

const BranchSelector: React.FC<BranchSelectorProps> = ({
  organizationId,
  applicationId,
  repoFullName,
  provider,
  currentBranch,
  onBranchUpdated,
  gitlabInstanceUrl,
  readOnly = false,
  variant = 'badge',
  collapsed = false,
}) => {
  const api = useAuthenticatedApi();
  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [branches, setBranches] = useState<BranchEntry[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchBranches = useCallback(async () => {
    setIsLoadingBranches(true);
    setLoadError(null);
    try {
      const endpoint = repoBranchesEndpoint(organizationId, provider, repoFullName, gitlabInstanceUrl);
      const data = await api.get<BranchEntry[]>(endpoint);
      setBranches(data);
    } catch (err: any) {
      console.error('Failed to load branches:', err);
      if (isFormProvider(provider) && err?.status === 401) {
        setLoadError(credentialsInvalidMessage(provider));
      } else {
        setLoadError(err?.message || 'Failed to load branches');
      }
    } finally {
      setIsLoadingBranches(false);
    }
  }, [api, organizationId, repoFullName, provider, gitlabInstanceUrl]);

  const handleOpen = () => {
    setIsOpen(true);
    setSearch('');
    fetchBranches();
  };

  const handleSelectBranch = async (branchName: string) => {
    if (branchName === currentBranch) {
      setIsOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      const updated = await api.patch<ApplicationResponse>(
        apiEndpoints.applications.updateRepoBranch(organizationId, applicationId, repoFullName),
        { default_branch: branchName },
      );
      onBranchUpdated(updated);
      toast.success(`Branch changed to ${branchName}`);
      setIsOpen(false);
    } catch (err: any) {
      console.error('Failed to update branch:', err);
      toast.error(err.message || 'Failed to update branch');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredBranches = search
    ? branches.filter((branch) => branch.name.toLowerCase().includes(search.toLowerCase()))
    : branches;

  return (
    <div ref={containerRef} className={variant === 'sidebar' ? 'relative' : 'relative inline-block'}>
      {variant === 'sidebar' ? (
        /* Full-width sidebar row with a "BRANCH" eyebrow */
        <button
          onClick={handleOpen}
          disabled={isSaving || readOnly}
          className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[10px] transition-colors hover:bg-[var(--surface-2)] disabled:opacity-60 disabled:cursor-not-allowed ${
            collapsed ? 'justify-center group-hover:justify-start' : ''
          }`}
          style={{ border: '1px solid var(--border-subtle)' }}
          title="Change branch"
          aria-label="Switch branch"
        >
          {isSaving ? (
            <Spinner size={16} tone="current" className="flex-shrink-0" />
          ) : (
            <GitBranch className="w-4 h-4 flex-shrink-0 text-[color:var(--text-secondary)]" />
          )}
          <div className={`flex-1 min-w-0 text-left leading-tight ${collapsed ? 'hidden group-hover:block' : ''}`}>
            <div className="text-[9px] font-bold uppercase tracking-wider text-[color:var(--text-muted)]">
              Branch
            </div>
            <div className="text-[13.5px] font-semibold truncate text-[color:var(--text-primary)]" style={{ fontFamily: 'var(--font-mono)' }}>
              {currentBranch}
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 flex-shrink-0 text-[color:var(--text-muted)] ${collapsed ? 'hidden group-hover:block' : ''}`} />
        </button>
      ) : (
        /* Trigger button styled as a badge */
        <button
          onClick={handleOpen}
          disabled={isSaving || readOnly}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs bg-neutral-200 dark:bg-ui-background-tertiary text-ui-text-secondary rounded hover:bg-ui-background-tertiary transition-colors cursor-pointer group disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-ui-background-tertiary dark:disabled:hover:bg-ui-background-tertiary"
          title="Change branch"
        >
          {isSaving ? (
            <Spinner size={12} tone="current" />
          ) : (
            <GitBranch className="w-3 h-3" />
          )}
          {currentBranch}
          <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      )}

      {/* Dropdown */}
      <PopoverPortal
        anchorRef={containerRef}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        width={256}
        className="bg-ui-background-secondary border border-ui-border-primary rounded-lg shadow-lg overflow-hidden"
      >
          {/* Search input */}
          <div className="p-2 border-b border-ui-border-primary">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter branches..."
              autoFocus
              className="w-full px-2.5 py-1.5 text-xs border border-ui-border-primary rounded bg-ui-background-secondary text-ui-text-primary placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary focus:ring-1 focus:ring-brand-primary focus:border-transparent outline-none"
            />
          </div>

          {/* Branch list */}
          <div className="max-h-48 overflow-y-auto">
            {isLoadingBranches ? (
              <div className="flex items-center justify-center py-6">
                <Spinner size={16} />
              </div>
            ) : loadError ? (
              <div className="px-3 py-4 text-center">
                <p className="text-xs text-red-500">{loadError}</p>
                <button
                  onClick={fetchBranches}
                  className="mt-1 text-xs text-brand-primary hover:underline"
                >
                  Retry
                </button>
              </div>
            ) : filteredBranches.length === 0 ? (
              <p className="text-xs text-ui-text-tertiary text-center py-4">
                {search ? 'No matching branches' : 'No branches found'}
              </p>
            ) : (
              filteredBranches.map((branch) => {
                const isActive = branch.name === currentBranch;
                return (
                  <button
                    key={branch.name}
                    onClick={() => handleSelectBranch(branch.name)}
                    disabled={isSaving}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-ui-background-tertiary transition-colors ${
                      isActive
                        ? 'bg-brand-primary/10 text-brand-primary dark:text-brand-primaryDarkMode font-medium'
                        : 'text-ui-text-secondary'
                    }`}
                  >
                    <span className="truncate">{branch.name}</span>
                    {isActive && <Check className="w-3.5 h-3.5 shrink-0 ml-2" />}
                  </button>
                );
              })
            )}
          </div>
      </PopoverPortal>
    </div>
  );
};

export default BranchSelector;
